import { HttpStatusCode } from 'axios';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';

import { analytics } from 'analytics';
import { useAppEnvironmentContext } from 'contexts/appEnvironmentContext';

import { Sankey } from '../generated-api';
import { isNumeric } from './number';

export enum Path {
  Sankeys = '/sankeys/',
  SankeyMainExample = '/sankeys/main-example/',
  Sankey = '/sankeys/:sankeyId/',
  SankeyIFramed = '/sankeys/:sankeyId/iframed/',
  SignInGoogleRedirect = '/login/google/',
  SignInMicrosoftRedirect = '/login/microsoft/',
  SignInWithEmail = '/login/email/',
  SignUpWithEmail = '/signup/email/',
  SubscriptionsCheckoutRedirect = '/subscriptions/checkout-redirect/:priceId/',
  SubscriptionsCheckoutSuccess = '/subscriptions/checkout-success/',
  SubscriptionsCheckoutCanceled = '/subscriptions/checkout-canceled/',
  // Add-in specific routes
  AddinHome = '/',
  SankeyPreview = '/sankeys/:sankeyId/preview/',
  // Web specific routes
  SankeysEditorRedirect = '/sankeys/editor/',
  // Legacy routes
  LegacyAddinSignIn = '/addin/signin/',
  LegacyAddinSignInWithPassword = '/addin/signin/password/',
  LegacySankeysOverview = '/sankeys/overview/',
}

export const pathToSankey = (sankeyId: number) => `/sankeys/${sankeyId}/`;
export const pathToSankeyPreview = (sankeyId: number) =>
  `/sankeys/${sankeyId}/preview/`;
export const pathToSankeyIFramed = (sankeyId: number) =>
  `/sankeys/${sankeyId}/iframed/`;
export const pathToSubscriptionCheckout = (priceId: string, quantity: number) =>
  `/subscriptions/checkout-redirect/${priceId}?quantity=${quantity}`;

// e.g. websiteLinkTo('/sankeys/1/') => 'https://www.sankeyart.com/sankeys/1/'
export const websiteLinkTo = (path: string) =>
  `${window.location.origin}${path}`;

// This hook is used to get the current Sankey ID from the URL
export const useCurrentRouteSankeyId = () => {
  const { sankeyId } = useParams<{ sankeyId: string }>();
  if (!isNumeric(sankeyId)) {
    throw new Error(`Invalid Sankey ID: ${sankeyId}`);
  }
  return Number(sankeyId);
};

// This hook is used to get the Sankey provided by the React Router loader
export const useLoadedSankey = () => {
  const sankey = useLoaderData() as Sankey | undefined;
  if (!sankey) {
    throw new Error('Sankey not found');
  }
  return sankey;
};

export const getURLHashRoute = () => window.location.hash.substring(1);

/*
 * This hook redirects users to the sign in page if they hit 403 error
 * and reports the error regularly in case of any other error codes.
 */
export const useAuthorizationCheck = () => {
  const { appEnvironment } = useAppEnvironmentContext();
  const navigate = useNavigate();

  return (error: any, message: string) => {
    switch (error?.response?.status) {
      case HttpStatusCode.Forbidden:
        // TODO: Update this after https://github.com/sankeyart/sankeyart/issues/1533
        if (appEnvironment === 'office') {
          // If the user in the add-in environment, just take them to the main page
          navigate(Path.AddinHome, { replace: true });
        } else {
          // Otherwise take them to the web sign in with a backlink to overview page
          window.location.href = websiteLinkTo(
            `/accounts/login/?next=${Path.Sankeys}`
          );
        }
        break;
      default:
        analytics.reportError(message, { error });
    }
  };
};
